import { Grid, Typography, Box, Tab, Tabs } from "@mui/material";
import { useState, useEffect } from "react";
import TestCasesTable from "./TestCasesTable";
import { ResultCard, CardType } from "../ResultCard";
import ResultChart from "../ResultChart";
import SummaryTable from "../SummaryTable";
import { useAppContext } from "../../../context/AppContext";
import MainCard from "../../../components/MainCard";
import Loader from "../../../components/Loader";

import ReportAnalysisContent from "./ReportAnalysisContent";
import { supportsRetryColumn } from "../../../utils/runnerReportUtils";

const WebMobileReport = (props) => {
  const [currentTab, setCurrentTab] = useState(0);
  const { testCases, totalTestCases, iteration, isLoading, project } =
    useAppContext();

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const isReportAnalysisEnabled = project?.enableReportAnalysis ?? false;

  useEffect(() => {
    if (!isReportAnalysisEnabled && currentTab === 1) {
      setCurrentTab(0);
    }
  }, [isReportAnalysisEnabled, currentTab]);

  return isLoading && !iteration && !testCases ? (
    <Loader />
  ) : (
    <>
      <Grid item xs={12} sx={{ mb: 1 }}>
        <MainCard content={false}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pr: 2,
            }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  minHeight: 38,
                },
              }}>
              <Tab label="Details" />
              {isReportAnalysisEnabled && <Tab label="Report Analysis" />}
            </Tabs>
          </Box>
        </MainCard>
      </Grid>

      {currentTab === 0 && (
        <>
          <Grid item xs={12}>
            <Grid container spacing={1} sx={{ alignItems: "stretch" }}>
              {/* Determine if we should show flaky card (Playwright iterations) */}
              {(() => {
                const hasFlaky = iteration?.flakyCount !== undefined && iteration?.flakyCount > 0;
                const gridSize = hasFlaky ? 2 : 2.4; // 6 cards vs 5 cards

                return (
                  <>
                    <Grid item xs={12} sm={6} md={gridSize} sx={{ display: "flex" }}>
                      <ResultCard
                        isLoading={isLoading}
                        count={totalTestCases || iteration?.totalCount}
                        cardType={"total"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={gridSize} sx={{ display: "flex" }}>
                      <ResultCard
                        isLoading={isLoading}
                        count={iteration?.passCount ?? 0}
                        cardType={"success"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={gridSize} sx={{ display: "flex" }}>
                      <ResultCard
                        isLoading={isLoading}
                        count={iteration?.failCount ?? 0}
                        cardType={"fail"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={gridSize} sx={{ display: "flex" }}>
                      <ResultCard
                        isLoading={isLoading}
                        count={iteration?.skipCount ?? 0}
                        cardType={"skip"}
                      />
                    </Grid>
                    {hasFlaky && (
                      <Grid item xs={12} sm={6} md={gridSize} sx={{ display: "flex" }}>
                        <ResultCard
                          isLoading={isLoading}
                          count={iteration?.flakyCount ?? 0}
                          cardType={"flaky"}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={gridSize} sx={{ display: "flex" }}>
                      <ResultCard
                        isLoading={isLoading}
                        count={(() => {
                          const total =
                            (iteration?.passCount ?? 0) +
                            (iteration?.failCount ?? 0) +
                            (iteration?.skipCount ?? 0);
                          return total > 0
                            ? ((iteration.passCount / total) * 100).toFixed(1)
                            : "0.0";
                        })()}
                        cardType={"rate"}
                      />
                    </Grid>
                  </>
                );
              })()}
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Grid container spacing={1} sx={{ alignItems: "stretch" }}>
              <Grid item md={6} xs={12} sx={{ display: "flex" }}>
                {iteration && (
                  <SummaryTable isLoading={isLoading} iteration={iteration} />
                )}
              </Grid>
              <Grid item md={6} xs={12} sx={{ display: "flex" }}>
                {iteration && (totalTestCases || iteration?.totalCount) > 0 ? (
                  <ResultChart
                    isLoading={isLoading}
                    passCount={iteration?.passCount ?? 0}
                    failCount={iteration?.failCount ?? 0}
                    skipCount={iteration?.skipCount ?? 0}
                  />
                ) : (
                  <MainCard
                    sx={{
                      minHeight: "100%",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}>
                    <Typography variant="body1" align="center">
                      No data available.
                    </Typography>
                  </MainCard>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <TestCasesTable
              testCases={testCases || []}
              supportsRetries={supportsRetryColumn(iteration, testCases)}
            />
          </Grid>
        </>
      )}

      {currentTab === 1 && isReportAnalysisEnabled && (
        <Grid item xs={12}>
          <ReportAnalysisContent
            testCases={testCases || []}
            iteration={iteration}
          />
        </Grid>
      )}
    </>
  );
};

export default WebMobileReport;

